/**
 * Refetch-on-focus pattern with skip-first-render, staleness guard, and
 * in-flight dedup. Used by screens that pair `fetchPolicy: 'cache-first'`
 * for the initial mount with a fresh fetch every time the user navigates
 * back to the screen.
 *
 * Introduced after PR #4983 (TA-9434) copy-pasted the same 6-line
 * pattern into 6 screens (AllFiles, RecentFiles, AllProjects, Tasks,
 * Tasks.web, TeamMembers). One regression in the dedup logic now means
 * one place to fix and one place to test.
 *
 * Staleness guard added in TA-10046: rapid tab switching on Tasks.web was
 * firing three simultaneous query pairs on every focus event. A 30-second
 * minimum window prevents the storm; the in-flight flag drops duplicate
 * focus events that arrive while a refetch is already running.
 *
 * Pass stalenessMs = 0 at stack-nav call sites (TeamMembers, AllFiles,
 * RecentFiles, ArchivedProjects) so navigating back always refetches — the
 * storm only occurs with web tab switching, not stack pops.
 * The clock only advances on success so a failed refetch does not block
 * retries for the full staleness window.
 *
 * The Tasks list (native Tasks.tsx and Tasks.web.tsx) is the exception: it
 * passes the shared TASK_LIST_REFETCH_MIN_AGE_MS window so a quick task
 * detail → back round-trip does not fire a redundant listTasks (TA-10055).
 *
 * ProjectDetails passes PROJECT_DETAIL_REFETCH_MIN_AGE_MS (TA-10306): the
 * app-wide projectUpdated subscription (useProjectMemberAddedNotification)
 * already patches this Project entity into the cache in real time, so this
 * refetch is a safety net for gaps that patch doesn't cover — not the
 * primary refresh path.
 *
 * Usage:
 *   const { refetch } = useListXQuery({ fetchPolicy: 'cache-first' });
 *   useRefetchOnFocus(refetch);
 *   // or with a custom staleness window:
 *   useRefetchOnFocus(refetch, 60_000);
 *
 * Behavior:
 *   - first focus (initial mount): refetch is NOT called
 *   - subsequent focus, data is stale: refetch fires once
 *   - subsequent focus, data is fresh (within stalenessMs): skipped
 *   - focus arrives while refetch in flight: skipped
 *   - any rejection from refetch is swallowed (the screen already
 *     surfaces loading/error from the underlying query)
 */

import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useRef } from 'react';

export const useRefetchOnFocus = (
  refetch: () => Promise<unknown>,
  stalenessMs = 30_000
): void => {
  const isMounted = useRef(false);
  // Stamped with Date.now() on the initial cache-first mount so the freshness
  // clock starts ticking from the load that already populated the screen. A
  // quick detail → back round-trip within stalenessMs is then treated as fresh
  // (TA-10055). stalenessMs = 0 call sites still always refetch, since
  // Date.now() - lastRefetchedAt is never < 0. The initial 0 is never read —
  // the mount skip overwrites it before any staleness check runs.
  const lastRefetchedAt = useRef(0);
  const inFlight = useRef(false);

  useFocusEffect(
    useCallback(() => {
      if (!isMounted.current) {
        isMounted.current = true;
        lastRefetchedAt.current = Date.now();
        return;
      }
      if (inFlight.current) return;
      if (Date.now() - lastRefetchedAt.current < stalenessMs) return;

      inFlight.current = true;
      refetch()
        .then(() => {
          lastRefetchedAt.current = Date.now();
        })
        .catch(() => undefined)
        .finally(() => {
          inFlight.current = false;
        });
    }, [refetch, stalenessMs])
  );
};
